import { HashBounds } from './HashBounds'
import SchemerModel from '../../core/SchemerModel'

const MIN_CELL_HEIGHT = 40
const BASE_ROW_HEADER_WIDTH = 40

export type TableMetric = [
  string,  // (key|id)
  number,  // index
  number,  // (width|height)
  number,  // offset(Left|Top)
  boolean, // isSticky
  number   // stickyOffset(Left|Top)
]

export type CellMetrics = {
  cellKey: string,
  columnKey: string
  rowId: string
  width: number
  height: number
  x: number
  y: number
  stickyRowOffsetTop: number
  stickyColOffsetLeft: number
}

export interface TableMetrics {
  hashBounds: HashBounds,
  rowMetrics: {[key: string]: TableMetric}
  columnMetrics: {[key: string]: TableMetric}
  numColumns: number,
  numRows: number,
  headerHeight: number
  totalHeight: number
  totalWidth: number
  totalStickyHeight: number
  totalStickyWidth: number
  actualStuckRowIds: string[]
  actualStuckColumnKeys: string[]
  rowHeaderWidth: number
}

export const getCellKey = (rowId: string, columnKey: string) => `row_${rowId}-col_${columnKey}`

export const getTableMetrics = (
  data: Map<string, SchemerModel>,
  stickyRowIds: string[] = [],
  stickyColumnKeys: string[] = [],
  includeRowHeader: boolean = false // If true, renders an index "header" for each row
) : TableMetrics | undefined => {
  if (data.size === 0) {
    return undefined
  }
  const actualStuckRowIds: string[] = []
  const actualStuckColumnKeys: string[] = []
  const rowIds = Array.from(data.keys())
  const firstRow = data.get(rowIds[0])
  const columns = firstRow ? firstRow.tableColumns : new Map()
  
  const rowHeaderWidth = (firstRow && !!firstRow.itemActions ? (firstRow.itemActionsWidth || 30) : 0) + BASE_ROW_HEADER_WIDTH

  // Pre-compute semi-dynamic row heights and cell offsets
  let colOffsetLeft = includeRowHeader ? rowHeaderWidth : 0
  let stickyColOffsetLeft = includeRowHeader ? rowHeaderWidth : 0 // Start offset from the row header
  let headerHeight = MIN_CELL_HEIGHT

  const columnMetricsByKey : {[key: string]: TableMetric} = {}

  const columnKeys = Array.from(columns.keys())
  const columnMetrics = columnKeys.map((columnKey: string, i: number) : TableMetric => {
    const columnConfig = columns.get(columnKey)
    const columnWidth = columnConfig.width || 200
    const isSticky = stickyColumnKeys.includes(columnKey) || columnConfig.forceSticky === true

    const colMetric : TableMetric = [
      columnKey,
      i,
      columnWidth,
      colOffsetLeft,
      isSticky,
      stickyColOffsetLeft
    ]

    colOffsetLeft += columnWidth
    if (isSticky) {
      stickyColOffsetLeft += columnWidth
      actualStuckColumnKeys.push(columnKey)
    }

    const headerCellHeight = columnConfig.headerHeight || MIN_CELL_HEIGHT 
    headerHeight = Math.max(headerCellHeight, headerHeight)

    columnMetricsByKey[columnKey] = colMetric
    return colMetric
  })

  let rowOffsetTop = headerHeight
  let stickyRowOffsetTop = headerHeight
  let rowMetricsById : {[key: string]: TableMetric} = {}
  let rowMetrics : TableMetric[] = []

  for (var [rowId, row] of data) {    
    const rowIndex = rowIds.indexOf(rowId)
    const isSticky = stickyRowIds.includes(rowId)
    const rowHeight = columnKeys.reduce((maxHeight, columnKey, columnIndex) => {
      const columnConfig = columns.get(columnKey)
      const columnHeight = columnConfig.height || MIN_CELL_HEIGHT
      const cellHeight = typeof columnHeight === 'function' ? columnHeight(row[columnKey], row) : columnHeight
      return Math.max(
        Math.max(
          cellHeight,
          MIN_CELL_HEIGHT
        ), 
        maxHeight
      )
    }, MIN_CELL_HEIGHT)

    const rowMetric : TableMetric = [
      rowId,
      rowIndex,
      rowHeight,
      rowOffsetTop,
      isSticky,
      stickyRowOffsetTop
    ]

    rowOffsetTop += rowHeight
    if (isSticky) {
      stickyRowOffsetTop += rowHeight
      actualStuckRowIds.push(rowId)
    }

    rowMetricsById[rowId] = rowMetric
    rowMetrics.push(rowMetric)
  }

  const totalHeight = rowOffsetTop
  const totalWidth = colOffsetLeft

  
  let hashBounds = new HashBounds(10, 2, -1, -1) //, totalWidth, totalHeight) // size of base cells (In squares of 2), amount of levels, Preload valueX,Y

  for (let rowIndex = 0; rowIndex < rowMetrics.length; rowIndex++) {
    const rowMetric = rowMetrics[rowIndex]

    for (let colIndex = 0; colIndex < columnMetrics.length; colIndex++) {
      const columnMetric = columnMetrics[colIndex];
      const rowId = rowMetric[0]
      const columnKey = columnMetric[0]
      const dims = {
        x: columnMetric[3],
        y: rowMetric[3],
        width: columnMetric[2],
        height: rowMetric[2]
      }
      const cellKey = getCellKey(rowId, columnKey)
      const cellData: CellMetrics = {
        cellKey: cellKey,
        rowId: rowId,
        columnKey: columnKey,
        stickyRowOffsetTop: rowMetric[5],
        stickyColOffsetLeft: columnMetric[5],
        x: columnMetric[3],
        y: rowMetric[3],
        width: columnMetric[2],
        height: rowMetric[2]
      }
      hashBounds.insert(cellData, dims)
    }
  }

  return {
    hashBounds,
    rowMetrics: rowMetricsById,
    columnMetrics: columnMetricsByKey,
    numColumns: columns.size,
    numRows: data.size,
    headerHeight,
    totalHeight,
    totalWidth,
    totalStickyHeight: stickyRowOffsetTop,
    totalStickyWidth: stickyColOffsetLeft,
    actualStuckRowIds,
    actualStuckColumnKeys,
    rowHeaderWidth
  }
}